import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, Typography, Button, Spinner } from '@material-tailwind/react';
import { CommandLineIcon, PlayIcon, ArrowDownTrayIcon, TableCellsIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

/*
 * VISTA MOCK — Disparador de Stored Procedures (reportes parametrizables).
 * Idea del cliente: una "librería" de SPs reutilizables que usuarios capacitados
 * ejecutan escribiendo el EXEC con parámetros, ven el resultado en una grilla y
 * lo descargan a Excel.
 *
 * Los procedimientos y resultados de abajo son DATOS DE EJEMPLO (mock) para
 * mostrar el diseño. En la versión real:
 *   - El listado saldría de `spWebListarProcedimientosExternos`.
 *   - La ejecución mandaría el EXEC al backend y devolvería el recordset real.
 */
const PROCEDIMIENTOS = [
    {
        nombre: 'spWebReporteEmailsPorBalance',
        titulo: 'Emails por balance',
        descripcion: 'Lista los correos de las iglesias según el estado de un balance, filtrado por región. Útil para enviar comunicaciones a quienes presentaron (o no) el balance.',
        parametros: [
            { nombre: '@id_region', tipo: 'int', detalle: 'Región a consultar' },
            { nombre: '@id_balance', tipo: 'int', detalle: 'Período / balance' },
        ],
        ejemploExec: 'EXEC spWebReporteEmailsPorBalance @id_region=1, @id_balance=2024',
        columnas: ['id_iglesia', 'iglesia', 'region', 'email', 'presento'],
        filas: [
            [1096, 'Templo Vida Abundante', 'Región 9', 'contacto@vidaabundante.org', 'Sí'],
            [1152, 'Iglesia Central', 'Región 7', 'central@uad.org', 'No'],
            [1187, 'Casa de Oración', 'Región 1', 'casaoracion@uad.org', 'Sí'],
        ],
    },
    {
        nombre: 'spWebIngresosPorRegion',
        titulo: 'Ingresos por región',
        descripcion: 'Resumen de ingresos por región para un mes y un año determinados.',
        parametros: [
            { nombre: '@id_region', tipo: 'int', detalle: 'Región a consultar' },
            { nombre: '@mes', tipo: 'int', detalle: 'Mes (1-12)' },
            { nombre: '@año', tipo: 'int', detalle: 'Año (ej. 2024)' },
        ],
        ejemploExec: 'EXEC spWebIngresosPorRegion @id_region=1, @mes=01, @año=2024',
        columnas: ['region', 'mes', 'anio', 'total_ingresos', 'cant_iglesias'],
        filas: [
            ['Región 1', 1, 2024, '$ 1.250.000,00', 42],
            ['Región 1', 2, 2024, '$ 1.090.500,00', 40],
        ],
    },
    {
        nombre: 'spWebObrerosSinCredencial',
        titulo: 'Obreros sin credencial vigente',
        descripcion: 'Obreros activos cuya credencial no está vigente, por región.',
        parametros: [
            { nombre: '@id_region', tipo: 'int', detalle: 'Región a consultar' },
        ],
        ejemploExec: 'EXEC spWebObrerosSinCredencial @id_region=7',
        columnas: ['id_obrero', 'obrero', 'region', 'rango', 'vencimiento'],
        filas: [
            [8475, 'Abelando Andrea', 'Región 7', 'Pastor', '12/03/2025'],
            [9012, 'Gómez Juan Carlos', 'Región 7', 'Evangelista', '01/01/2024'],
        ],
    },
];

export default function ReportesSP() {
    const [seleccionado, setSeleccionado] = useState(PROCEDIMIENTOS[0]);
    const [consola, setConsola] = useState(PROCEDIMIENTOS[0].ejemploExec);
    const [resultado, setResultado] = useState(null);
    const [ejecutando, setEjecutando] = useState(false);

    const seleccionarProc = (proc) => {
        setSeleccionado(proc);
        setConsola(proc.ejemploExec);
        setResultado(null);
    };

    const ejecutar = () => {
        // MOCK: simula la ejecución y devuelve el resultado de ejemplo del SP seleccionado.
        setEjecutando(true);
        setResultado(null);
        setTimeout(() => {
            setResultado({ columnas: seleccionado.columnas, filas: seleccionado.filas });
            setEjecutando(false);
        }, 450);
    };

    const exportarExcel = () => {
        if (!resultado) return;
        const data = resultado.filas.map((fila) => {
            const obj = {};
            resultado.columnas.forEach((col, i) => { obj[col] = fila[i]; });
            return obj;
        });
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Resultado');
        XLSX.writeFile(wb, `${seleccionado.nombre}.xlsx`);
    };

    return (
        <div className="w-full max-w-[1400px] mx-auto p-4">
            {/* Encabezado */}
            <div className="flex items-center gap-3 mb-1">
                <CommandLineIcon className="h-7 w-7 text-blue-600" />
                <Typography variant="h4" color="blue-gray" className="font-bold">
                    Reportes / Procedimientos
                </Typography>
                <span className="ml-2 text-[11px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
                    Vista preliminar (mock)
                </span>
            </div>
            <Typography variant="small" color="gray" className="mb-6">
                Librería de procedimientos reutilizables. Seleccione uno, revise su explicación, ajuste los parámetros y ejecútelo para ver el resultado y exportarlo a Excel.
            </Typography>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Listbox de procedimientos */}
                <Card className="col-span-1 lg:col-span-4 p-0 border border-gray-200 shadow-sm overflow-hidden h-fit">
                    <div className="bg-gray-100 border-b border-gray-200 px-4 py-3">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Procedimientos disponibles</span>
                    </div>
                    <ul className="max-h-[420px] overflow-y-auto">
                        {PROCEDIMIENTOS.map((proc) => (
                            <li
                                key={proc.nombre}
                                onClick={() => seleccionarProc(proc)}
                                className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${seleccionado.nombre === proc.nombre ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <DocumentTextIcon className="h-4 w-4 text-blue-500 shrink-0" />
                                    <span className={`text-sm font-semibold ${seleccionado.nombre === proc.nombre ? 'text-blue-800' : 'text-gray-800'}`}>{proc.titulo}</span>
                                </div>
                                <span className="block text-[11px] text-gray-400 font-mono mt-0.5 truncate">{proc.nombre}</span>
                            </li>
                        ))}
                    </ul>
                </Card>

                {/* Detalle + consola + resultados */}
                <div className="col-span-1 lg:col-span-8 flex flex-col gap-6">
                    {/* Explicación del SP */}
                    <Card className="p-5 border border-gray-200 shadow-sm">
                        <Typography variant="h6" color="blue-gray" className="font-semibold">
                            {seleccionado.titulo}
                        </Typography>
                        <span className="text-[11px] text-gray-400 font-mono">{seleccionado.nombre}</span>
                        <Typography variant="small" color="gray" className="mt-2">
                            {seleccionado.descripcion}
                        </Typography>
                        <div className="mt-4">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Parámetros</span>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {seleccionado.parametros.map((p) => (
                                    <span key={p.nombre} className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded px-2 py-1 text-xs">
                                        <span className="font-mono font-semibold text-blue-700">{p.nombre}</span>
                                        <span className="text-gray-400">{p.tipo}</span>
                                        <span className="text-gray-500">· {p.detalle}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Card>

                    {/* Consola */}
                    <Card className="p-0 border border-gray-200 shadow-sm overflow-hidden">
                        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-200 uppercase tracking-wider flex items-center gap-2">
                                <CommandLineIcon className="h-4 w-4" /> Consola de ejecución
                            </span>
                        </div>
                        <textarea
                            value={consola}
                            onChange={(e) => setConsola(e.target.value)}
                            rows={2}
                            spellCheck={false}
                            className="w-full bg-gray-900 text-green-300 font-mono text-sm p-4 outline-none resize-none"
                        />
                        <div className="flex justify-end gap-2 p-3 bg-gray-50 border-t border-gray-200">
                            <Button
                                size="sm"
                                color="blue"
                                className="flex items-center gap-2"
                                onClick={ejecutar}
                                disabled={ejecutando}
                            >
                                {ejecutando ? <Spinner className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                                Ejecutar
                            </Button>
                        </div>
                    </Card>

                    {/* Resultados */}
                    <Card className="p-0 border border-gray-200 shadow-sm overflow-hidden">
                        <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                                <TableCellsIcon className="h-4 w-4" /> Resultados
                            </span>
                            <Button
                                size="sm"
                                color="green"
                                variant="gradient"
                                className="flex items-center gap-2"
                                onClick={exportarExcel}
                                disabled={!resultado}
                            >
                                <ArrowDownTrayIcon className="h-4 w-4" /> Exportar Excel
                            </Button>
                        </div>

                        {ejecutando ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                <Spinner className="h-8 w-8" />
                                <span className="mt-3 text-sm">Ejecutando...</span>
                            </div>
                        ) : !resultado ? (
                            <div className="py-12 text-center text-gray-400 text-sm italic">
                                Ejecute un procedimiento para ver los resultados.
                            </div>
                        ) : (
                            <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                                        <tr>
                                            {resultado.columnas.map((col) => (
                                                <th key={col} className="px-4 py-2 border-b border-gray-200 font-semibold whitespace-nowrap">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resultado.filas.map((fila, i) => (
                                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                                {fila.map((celda, j) => (
                                                    <td key={j} className="px-4 py-2 text-gray-700 whitespace-nowrap">{celda}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100">
                                    {resultado.filas.length} fila(s)
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}
